/**
 * New Mandate Form Component
 * Full form for creating a new consultant mandate
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { db } from '@/lib/firebase';
import { collection, addDoc, getDocs, query, where, serverTimestamp } from 'firebase/firestore';
import { COMPANY_CONFIG, formatCurrency, getDefaultCurrency } from '@/config/company.config';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarIcon, Plus, Trash2, Save, ArrowLeft, DollarSign } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Client {
  id: string;
  name: string;
  email: string;
  company?: string;
}

interface Milestone {
  id: string;
  title: string;
  description: string;
  dueDate: Date | null;
}

const MANDATE_TYPES = [
  'legal',
  'tax',
  'financial',
  'immigration',
  'due_diligence',
  'consulting',
  'other',
] as const;

const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'] as const;

const TRANSLATIONS = {
  en: {
    title: 'New Mandate',
    subtitle: 'Create a new mandate for your client',
    back: 'Back',
    selectClient: 'Select Client',
    selectClientPlaceholder: 'Choose a client...',
    mandateTitle: 'Mandate Title',
    mandateTitlePlaceholder: 'e.g., Legal Due Diligence - Company ABC',
    mandateType: 'Mandate Type',
    selectTypePlaceholder: 'Select type...',
    description: 'Description',
    descriptionPlaceholder: 'Describe the scope of work...',
    priority: 'Priority',
    startDate: 'Start Date',
    dueDate: 'Due Date',
    selectDate: 'Select date',
    financialDetails: 'Financial Details',
    hourlyRate: 'Hourly Rate',
    estimatedHours: 'Estimated Hours',
    totalFee: 'Total Fee',
    currency: 'Currency',
    milestones: 'Milestones',
    addMilestone: 'Add Milestone',
    milestoneTitle: 'Milestone Title',
    milestoneTitlePlaceholder: 'e.g., Initial Review',
    milestoneDescription: 'Description',
    milestoneDescriptionPlaceholder: 'Milestone details...',
    milestoneDueDate: 'Due Date',
    removeMilestone: 'Remove',
    createMandate: 'Create Mandate',
    creating: 'Creating...',
    mandateCreated: 'Mandate Created',
    mandateCreatedDesc: 'Your new mandate has been created successfully',
    error: 'Error',
    errorDesc: 'Failed to create mandate',
    requiredField: 'This field is required',
    noClients: 'No clients found. Add a client first.',
    legal: 'Legal Services',
    tax: 'Tax Advisory',
    financial: 'Financial Analysis',
    immigration: 'Immigration Law',
    due_diligence: 'Due Diligence',
    consulting: 'Consulting',
    other: 'Other Services',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    urgent: 'Urgent',
  },
  fr: {
    title: 'Nouveau Mandat',
    subtitle: 'Créez un nouveau mandat pour votre client',
    back: 'Retour',
    selectClient: 'Sélectionner Client',
    selectClientPlaceholder: 'Choisissez un client...',
    mandateTitle: 'Titre du Mandat',
    mandateTitlePlaceholder: 'ex., Due Diligence Juridique - Société ABC',
    mandateType: 'Type de Mandat',
    selectTypePlaceholder: 'Sélectionner type...',
    description: 'Description',
    descriptionPlaceholder: 'Décrivez le travail à effectuer...',
    priority: 'Priorité',
    startDate: 'Date de Début',
    dueDate: 'Date Limite',
    selectDate: 'Sélectionner date',
    financialDetails: 'Détails Financiers',
    hourlyRate: 'Taux Horaire',
    estimatedHours: 'Heures Estimées',
    totalFee: 'Frais Totaux',
    currency: 'Devise',
    milestones: 'Jalons',
    addMilestone: 'Ajouter Jalon',
    milestoneTitle: 'Titre du Jalon',
    milestoneTitlePlaceholder: 'ex., Revue Initiale',
    milestoneDescription: 'Description',
    milestoneDescriptionPlaceholder: 'Détails du jalon...',
    milestoneDueDate: 'Date Limite',
    removeMilestone: 'Supprimer',
    createMandate: 'Créer Mandat',
    creating: 'Création...',
    mandateCreated: 'Mandat Créé',
    mandateCreatedDesc: 'Votre nouveau mandat a été créé avec succès',
    error: 'Erreur',
    errorDesc: 'Échec de la création du mandat',
    requiredField: 'Ce champ est requis',
    noClients: 'Aucun client trouvé. Ajoutez un client d\'abord.',
    legal: 'Services Juridiques',
    tax: 'Conseil Fiscal',
    financial: 'Analyse Financière',
    immigration: 'Droit Immigration',
    due_diligence: 'Due Diligence',
    consulting: 'Conseil',
    other: 'Autres Services',
    low: 'Basse',
    medium: 'Moyenne',
    high: 'Haute',
    urgent: 'Urgent',
  },
};

// Mock clients for demo
const mockClients: Client[] = [
  { id: 'client-1', name: 'Jean-Pierre Martin', email: 'jp.martin@example.com', company: 'Tech Solutions Inc.' },
  { id: 'client-2', name: 'Sophie Tremblay', email: 'sophie.t@example.com', company: 'Tremblay Holdings' },
  { id: 'client-3', name: 'Marc Dubois', email: 'marc.dubois@example.com', company: 'Dubois Enterprises' },
];

export default function NewMandateForm() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const t = TRANSLATIONS[language === 'fr' ? 'fr' : 'en'];

  // Form state
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [mandateTitle, setMandateTitle] = useState('');
  const [mandateType, setMandateType] = useState<string>('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<string>('medium');
  const [startDate, setStartDate] = useState<Date | undefined>(new Date());
  const [dueDate, setDueDate] = useState<Date | undefined>();
  const [hourlyRate, setHourlyRate] = useState<number>(250);
  const [estimatedHours, setEstimatedHours] = useState<number>(40);
  const [currency, setCurrency] = useState<string>(getDefaultCurrency());
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(true);

  // Load clients
  useEffect(() => {
    if (user?.email) {
      loadClients();
    }
  }, [user?.email]);

  const loadClients = async () => {
    if (!user?.email) return;
    setIsLoadingClients(true);
    try {
      const clientsQuery = query(
        collection(db, 'consultantClients'),
        where('consultantId', '==', user.email)
      );
      const snapshot = await getDocs(clientsQuery);
      const clientsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Client[];
      setClients(clientsList.length > 0 ? clientsList : mockClients);
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients(mockClients);
    } finally {
      setIsLoadingClients(false);
    }
  };

  const totalFee = hourlyRate * estimatedHours;

  const addMilestone = () => {
    const newMilestone: Milestone = {
      id: `milestone-${Date.now()}`,
      title: '',
      description: '',
      dueDate: null,
    };
    setMilestones([...milestones, newMilestone]);
  };

  const updateMilestone = (id: string, field: keyof Milestone, value: any) => {
    setMilestones(milestones.map(m => 
      m.id === id ? { ...m, [field]: value } : m
    ));
  };

  const removeMilestone = (id: string) => {
    setMilestones(milestones.filter(m => m.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.email || !selectedClient || !mandateTitle || !mandateType) {
      toast({
        title: t.error,
        description: t.requiredField,
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const selectedClientData = clients.find(c => c.id === selectedClient);
      
      await addDoc(collection(db, 'consultantMandates'), {
        consultantId: user.email,
        clientId: selectedClient,
        clientName: selectedClientData?.name || '',
        title: mandateTitle,
        type: mandateType,
        description,
        priority,
        status: 'pending',
        startDate: startDate || new Date(),
        dueDate: dueDate || null,
        hourlyRate,
        estimatedHours,
        hoursWorked: 0,
        totalFee,
        currency,
        milestones: milestones.map(m => ({
          ...m,
          completed: false,
        })),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      toast({
        title: t.mandateCreated,
        description: t.mandateCreatedDesc,
      });

      navigate('/dashboard/consultant/mandates');
    } catch (error) {
      console.error('Error creating mandate:', error);
      toast({
        title: t.error,
        description: t.errorDesc,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/dashboard/consultant/mandates')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t.back}
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t.title}</h1>
          <p className="text-muted-foreground">{t.subtitle}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Client Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t.selectClient}</CardTitle>
            </CardHeader>
            <CardContent>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger>
                  <SelectValue placeholder={t.selectClientPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      <div className="flex flex-col">
                        <span>{client.name}</span>
                        {client.company && (
                          <span className="text-xs text-muted-foreground">{client.company}</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Mandate Type */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t.mandateType}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Select value={mandateType} onValueChange={setMandateType}>
                <SelectTrigger>
                  <SelectValue placeholder={t.selectTypePlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {MANDATE_TYPES.map(type => (
                    <SelectItem key={type} value={type}>
                      {t[type as keyof typeof t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div>
                <Label>{t.priority}</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map(p => (
                      <SelectItem key={p} value={p}>
                        {t[p as keyof typeof t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Mandate Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t.mandateTitle}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Input
                value={mandateTitle}
                onChange={(e) => setMandateTitle(e.target.value)}
                placeholder={t.mandateTitlePlaceholder}
                required
              />
            </div>

            <div>
              <Label>{t.description}</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t.descriptionPlaceholder}
                rows={4}
                className="mt-1"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>{t.startDate}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal mt-1",
                        !startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, 'PPP', { locale: language === 'fr' ? fr : undefined }) : t.selectDate}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label>{t.dueDate}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal mt-1",
                        !dueDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dueDate ? format(dueDate, 'PPP', { locale: language === 'fr' ? fr : undefined }) : t.selectDate}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={dueDate}
                      onSelect={setDueDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Financial Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              {t.financialDetails}
            </CardTitle>
            <CardDescription>
              {t.currency}: {COMPANY_CONFIG.currency} ({COMPANY_CONFIG.currencySymbol})
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <Label>{t.hourlyRate} ({COMPANY_CONFIG.currencySymbol})</Label>
                <Input
                  type="number"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(Number(e.target.value))}
                  min={0}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>{t.estimatedHours}</Label>
                <Input
                  type="number"
                  value={estimatedHours}
                  onChange={(e) => setEstimatedHours(Number(e.target.value))}
                  min={0}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>{t.totalFee}</Label>
                <div className="mt-1 flex items-center h-10 px-3 rounded-md border bg-muted">
                  <span className="font-semibold">
                    {formatCurrency(totalFee, currency)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Milestones */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">{t.milestones}</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addMilestone}>
                <Plus className="mr-2 h-4 w-4" />
                {t.addMilestone}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {milestones.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                {language === 'fr' ? 'Aucun jalon ajouté' : 'No milestones added yet'}
              </p>
            ) : (
              milestones.map((milestone, index) => (
                <div key={milestone.id} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline">#{index + 1}</Badge>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeMilestone(milestone.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <Label>{t.milestoneTitle}</Label>
                      <Input
                        value={milestone.title}
                        onChange={(e) => updateMilestone(milestone.id, 'title', e.target.value)}
                        placeholder={t.milestoneTitlePlaceholder}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>{t.milestoneDueDate}</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal mt-1",
                              !milestone.dueDate && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {milestone.dueDate 
                              ? format(milestone.dueDate, 'PPP', { locale: language === 'fr' ? fr : undefined }) 
                              : t.selectDate}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={milestone.dueDate || undefined}
                            onSelect={(date) => updateMilestone(milestone.id, 'dueDate', date)}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <div>
                    <Label>{t.milestoneDescription}</Label>
                    <Textarea
                      value={milestone.description}
                      onChange={(e) => updateMilestone(milestone.id, 'description', e.target.value)}
                      placeholder={t.milestoneDescriptionPlaceholder}
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate('/dashboard/consultant/mandates')}
          >
            {language === 'fr' ? 'Annuler' : 'Cancel'}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            <Save className="mr-2 h-4 w-4" />
            {isSubmitting ? t.creating : t.createMandate}
          </Button>
        </div>
      </form>
    </div>
  );
}
